import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { DOTLOTTIE_LOADER } from '../../../tokens';

@Component({
  selector: 'qt-typing-lottie',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="typing-lottie"
      [style.width.px]="size()"
      [style.height.px]="size()">
      <canvas #canvas [attr.width]="size()" [attr.height]="size()"></canvas>
    </div>
  `,
  styles: [
    `
      .typing-lottie {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 48px;
        min-height: 48px;
      }
      .typing-lottie canvas {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    `,
  ],
})
export class TypingLottieComponent implements AfterViewInit, OnDestroy {
  private readonly canvasRef =
    viewChild<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly dotLottieLoader = inject(DOTLOTTIE_LOADER, {
    optional: true,
  });
  private readonly cdr = inject(ChangeDetectorRef);

  readonly src = input.required<string>();
  readonly size = input(56);

  private instance: { destroy: () => void } | null = null;
  private destroyed = false;

  ngAfterViewInit(): void {
    const canvas = this.canvasRef()?.nativeElement;
    const src = this.src();
    const loader = this.dotLottieLoader;
    if (!canvas || !src || !loader) return;

    const sizePx = this.size();
    canvas.width = sizePx;
    canvas.height = sizePx;

    loader()
      .then((mod: unknown) => {
        if (this.destroyed) return;
        const canvasEl = this.canvasRef()?.nativeElement;
        if (!canvasEl) return;
        canvasEl.width = sizePx;
        canvasEl.height = sizePx;
        const DotLottieCtor =
          (mod as { DotLottie?: unknown })?.DotLottie ??
          (mod as { default?: { DotLottie?: unknown } })?.default?.DotLottie ??
          (mod as { default?: unknown })?.default;
        if (typeof DotLottieCtor !== 'function') return;
        this.instance = new (DotLottieCtor as new (opts: {
          canvas: HTMLCanvasElement;
          src: string;
          autoplay: boolean;
          loop: boolean;
          renderConfig?: { autoResize?: boolean; devicePixelRatio?: number };
          layout?: { fit: string; align: [number, number] };
        }) => { destroy: () => void })({
          canvas: canvasEl,
          src,
          autoplay: true,
          loop: true,
          renderConfig: {
            autoResize: true,
            devicePixelRatio:
              typeof window !== 'undefined'
                ? (window.devicePixelRatio ?? 1)
                : 1,
          },
          layout: { fit: 'contain', align: [0.5, 0.5] },
        });
        this.cdr.markForCheck();
      })
      .catch(() => {
        // Loader failed; parent can fallback to dots
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.instance?.destroy) {
      this.instance.destroy();
      this.instance = null;
    }
  }
}
